import React, {
	useEffect,
	useRef,
	useState,
	useMemo,
	useCallback,
} from "react";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import { Icon, IconButton } from "@alm/flow-ds/components";
import axios from "axios";
import AWS from "aws-sdk";
import { useTranslation } from "react-i18next";
import api from "lib/api";

export type UploadSuccess = {
	id: string;
	label: string;
};

export type UploadSuccessCallback = (data: UploadSuccess) => void;

export interface UploadItemProps {
	file: File;
	onUploadStart?: () => void;
	onUploadComplete?: () => void;
	onUploadSuccess?: UploadSuccessCallback;
}

type Status =
	| "queued"
	| "processing"
	| "uploading"
	| "complete"
	| "aborted"
	| "error";

function UploadItem({
	file,
	onUploadStart,
	onUploadComplete,
	onUploadSuccess,
}: UploadItemProps) {
	const { t } = useTranslation();
	const [progress, setProgress] = useState(0);
	const [status, setStatus] = useState<Status>("queued");
	const uploadRef = useRef<null | AWS.S3.ManagedUpload>(null);

	const source = useMemo(() => {
		const CancelToken = axios.CancelToken;
		return CancelToken.source();
	}, []);

	const abort = useCallback(() => {
		uploadRef.current?.abort();
		setStatus("aborted");
	}, []);

	useEffect(() => {
		setStatus("processing");
		if (onUploadStart) onUploadStart();

		api.media
			.create(file.name, file.type, source.token)
			.then(res => {
				const { awsConfigs, params, id } = res.data;
				AWS.config.update(awsConfigs);

				const upload = new AWS.S3.ManagedUpload({
					params: {
						...params,
						Body: file,
						ContentType: file.type,
					},
				});

				uploadRef.current = upload;

				setStatus("uploading");

				upload.send(function (err, S3file) {
					if (err) {
						setStatus("error");
					} else {
						// Success
						if (onUploadSuccess)
							onUploadSuccess({ label: file.name, id });
						setStatus("complete");
					}
					if (onUploadComplete) onUploadComplete();
				});

				// listen to the AWS httpUploadProgress event, and emit an equivalent Dropzone event
				upload?.on(
					"httpUploadProgress",
					(progress: AWS.S3.ManagedUpload.Progress) => {
						setProgress(
							Math.floor((progress.loaded * 100) / progress.total)
						);
					}
				);
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					setStatus("error");
				}
				if (onUploadComplete) onUploadComplete();
			});

		return () => {
			source.cancel();
			uploadRef.current?.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [file, onUploadComplete, onUploadStart, source]);

	return (
		<div className="upload__item">
			<div className="upload__item-status">
				{status === "uploading" && (
					<CircularProgressbarWithChildren value={progress} />
				)}
				{status === "complete" && (
					<Icon
						type="check-circle"
						weight="l"
						className="upload__status-icon upload__status-icon--success"
					/>
				)}
				{status === "error" && (
					<Icon
						type="times-circle"
						weight="l"
						className="upload__status-icon upload__status-icon--error"
					/>
				)}
				{status === "aborted" && (
					<Icon
						type="ban"
						weight="l"
						className="upload__status-icon text-warning"
					/>
				)}
			</div>
			<div className="upload__item-details">
				<div className="upload__item-top">
					<div className="upload__item-name">{file.name}</div>
				</div>

				<div className="upload__status-desc small">
					{status === "queued" && (
						<span className="text-muted">
							{t("elements.media.uploadItemQueued")}
						</span>
					)}
					{status === "processing" && (
						<span className="text-muted">
							{t("elements.media.uploadItemProcessing")}
						</span>
					)}
					{status === "uploading" && (
						<span className="text-muted">
							{t("elements.media.uploadItemUploading")} {progress}
							%
						</span>
					)}
					{status === "complete" && (
						<span className="text-muted">{file.size}kb</span>
					)}
					{status === "error" && (
						<span className="text-danger">
							{t("elements.media.uploadItemError")}
						</span>
					)}
					{status === "aborted" && (
						<span className="text-muted">
							{t("elements.media.uploadItemAborted")}
						</span>
					)}
				</div>
			</div>
			<div className="upload__item-actions">
				{status === "uploading" && (
					<IconButton
						label={t("elements.media.uploadItemAbortLabel")}
						icon="times"
						onClick={abort}
						className="btn-default btn--round btn-sm upload__item-cancel"
					/>
				)}
			</div>
		</div>
	);
}

export default React.memo(UploadItem);
